import FadeInContent from '-/Animation/FadeInContent';
import Avatar from '-/Custom/Avatar';
import { type Href, Link } from 'expo-router';
import { useQuery } from 'urql';
import type { MemberType } from '@/components/People/PeopleFilterSelector';
import Item from '@/components/UI/PressableRow';
import Text from '@/components/UI/Text';
import Icon from '@/components/Unistyles/Icon';
import { graphql } from '@/gql/graphql';
import type { JoinKind } from '@/stores/useJoinIntentStore';
import { LONG_PRESS_DELAY } from '@/utils/constants';
import getAvatarText from '@/utils/getAvatarText';
import handleLongPress from '@/utils/handleLongPress';
import { getJoinKindConfiguration } from '@/utils/joinKind';
import parseCurrentInviteLink from '@/utils/parseCurrentInviteLink';

const GetInviteRichLink = graphql(/* GraphQL */ `
  query GetInviteRichLink($kind: invite_entry_kind!, $code: String!) {
    invite_entry_preview(args: { kind: $kind, code: $code }) {
      entity_id
      title
      is_member
    }
  }
`);

const GetInviteTeamAvatar = graphql(/* GraphQL */ `
  query GetInviteTeamAvatar($id: uuid!) {
    team_by_pk(id: $id) {
      id
      avatar_id
      home_primary_color
    }
  }
`);

function getInviteHref({
  code,
  entityId,
  isMember,
  kind,
  memberType,
}: {
  code: string;
  entityId: string | null | undefined;
  isMember: boolean;
  kind: JoinKind;
  memberType: MemberType | null;
}): Href {
  if (isMember && entityId) {
    return kind === 'organization'
      ? { pathname: '/organization', params: { organizationId: entityId } }
      : { pathname: '/team', params: { teamId: entityId } };
  }

  return {
    pathname: getJoinKindConfiguration(kind).joinPathname,
    params: {
      ...(code ? { code } : {}),
      ...(memberType ? { memberType } : {}),
    },
  };
}

export default function RichLink({
  content,
  onLongPress,
}: {
  content: string;
  onLongPress: (content: string) => void;
}) {
  const inviteLink = parseCurrentInviteLink(content);
  const inviteKind = inviteLink?.kind;
  const inviteCode = inviteLink?.code || '';

  const [{ data: inviteData, fetching: inviteFetching }] = useQuery({
    query: GetInviteRichLink,
    variables: { kind: inviteKind || 'team', code: inviteCode },
    pause: !inviteKind || inviteCode.length !== 8,
  });

  const invitePreview = inviteData?.invite_entry_preview[0];
  const teamId = inviteKind === 'team' ? invitePreview?.entity_id || '' : '';
  const [{ data: teamData, fetching: teamFetching }] = useQuery({
    query: GetInviteTeamAvatar,
    variables: { id: teamId },
    pause: inviteKind !== 'team' || !teamId,
  });
  const team = teamData?.team_by_pk;
  const isJoined = invitePreview?.is_member === true;
  const inviteTitle = invitePreview?.title
    ? `${isJoined ? 'Joined' : 'Join'} ${invitePreview.title}`
    : `${isJoined ? 'Joined' : 'Join'} ${inviteKind === 'organization' ? 'organization' : 'team'}`;

  if (!inviteKind) {
    return null;
  }

  return (
    <FadeInContent
      fetching={inviteFetching || (inviteKind === 'team' && teamFetching)}
    >
      <Link
        href={getInviteHref({
          code: inviteCode,
          entityId: invitePreview?.entity_id,
          isMember: isJoined,
          kind: inviteKind,
          memberType: inviteLink?.memberType ?? null,
        })}
        asChild
      >
        <Item
          delayLongPress={LONG_PRESS_DELAY}
          type="card"
          spaced
          onLongPress={handleLongPress(() => onLongPress(content))}
        >
          {inviteKind === 'team' ? (
            <Avatar
              id={team?.avatar_id || ''}
              color={team?.home_primary_color}
              name={getAvatarText(invitePreview?.title)}
              size="md"
            />
          ) : null}
          <Text flexGrow={1} flexShrink={1} numberOfLines={1} size="xl">
            {inviteTitle}
          </Text>
          {isJoined ? (
            <Icon name="checkmark-circle-outline" variant="success" />
          ) : null}
        </Item>
      </Link>
    </FadeInContent>
  );
}
